import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Bus, Loader2, CreditCard, Eye, AlertTriangle, Wallet, Receipt, MapPin } from 'lucide-react';
import PaymentModal from '../../components/student-portal/PaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentTransport() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [gatewayConfig, setGatewayConfig] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const [summaryRes, ledgerRes, gatewayRes] = await Promise.all([
        fetch(`${API_URL}/api/student-portal/transport/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/transport/ledger`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/payment/gateway-config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (ledgerRes.ok) {
        const data = await ledgerRes.json();
        setLedger(data.ledger || []);
      }
      if (gatewayRes.ok) setGatewayConfig(await gatewayRes.json());
    } catch (error) {
      console.error('Error fetching transport data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    fetchData();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!summary?.allocated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <h1 className="text-xl font-semibold">Transport</h1>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-6">
          <Card>
            <CardContent className="py-16 text-center">
              <Bus className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Transport Allocation</h3>
              <p className="text-gray-500">You don&apos;t have transport allocation.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const dueAmount = summary?.due || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-xl font-semibold">Transport</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bus className="w-5 h-5" /> Transport Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500">Route</p>
                <p className="font-medium">{summary.route}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Vehicle No</p>
                <p className="font-medium">{summary.vehicle_no}</p>
              </div>
              {summary.pickup_point && (
                <div>
                  <p className="text-sm text-gray-500">Pickup Point</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {summary.pickup_point}
                  </p>
                </div>
              )}
              {summary.pickup_time && (
                <div>
                  <p className="text-sm text-gray-500">Pickup Time</p>
                  <p className="font-medium">{summary.pickup_time}</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Total Fee</p>
                <p className="text-xl font-bold">₹{summary.total_fee?.toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-500">Paid</p>
                <p className="text-xl font-bold text-green-600">₹{summary.paid?.toLocaleString('en-IN')}</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-500">Due</p>
                <p className="text-xl font-bold text-red-600">₹{dueAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pay Online Section */}
        {dueAmount > 0 && gatewayConfig?.razorpay_enabled && (
          <Card className="mb-6 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 rounded-full">
                    <Wallet className="w-8 h-8 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Pay Transport Fee Online</h3>
                    <p className="text-sm text-gray-600">Quick & secure payment via Razorpay</p>
                    {gatewayConfig?.is_test_mode && (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded mt-1">
                        <AlertTriangle className="w-3 h-3" /> Test Mode
                      </span>
                    )}
                  </div>
                </div>
                <Button 
                  className="bg-orange-600 hover:bg-orange-700"
                  onClick={() => setShowPaymentModal(true)}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay ₹{dueAmount.toLocaleString('en-IN')} Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Damage Fine Alert */}
        {summary.damage_fine_due > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                  <div>
                    <p className="font-medium text-red-800">Damage Fine Due</p>
                    <p className="text-sm text-red-600">You have pending vehicle damage fine</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-red-600">₹{summary.damage_fine_due?.toLocaleString('en-IN')}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ledger */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Payment History</span>
              <Button variant="outline" size="sm" onClick={() => navigate('/student/receipts')}>
                <Receipt className="w-4 h-4 mr-1" />
                All Receipts
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ledger.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No payment records</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Receipt No</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Mode</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Amount</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {ledger.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm">{item.payment_date}</td>
                        <td className="px-4 py-3 text-sm text-blue-600">{item.receipt_no}</td>
                        <td className="px-4 py-3 text-sm">{item.payment_mode || 'Cash'}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-green-600">₹{item.amount?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-right">
                          {item.receipt_no && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate(`/student/receipt/${item.receipt_no}`)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        feeType="transport"
        description="Transport Fee Payment"
        amount={dueAmount}
        referenceId={summary?.allocation_id}
        isTestMode={gatewayConfig?.is_test_mode}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}
